import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { CreditCard, Link2, CheckCircle2, Building, Loader2, Trash2, RefreshCw, ExternalLink, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const getBackendUrl = () => {
  if (window.location.hostname !== 'localhost') {
    return window.location.origin;
  }
  return process.env.REACT_APP_BACKEND_URL || '';
};

const BACKEND_URL = getBackendUrl();

function XeroBillingAdmin() {
  const [searchParams] = useSearchParams();
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [contacts, setContacts] = useState([]);
  const [links, setLinks] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [linkingContact, setLinkingContact] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [tenantName, setTenantName] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    const init = async () => {
      try {
        if (searchParams.get('xero_connected') === 'true') {
          toast.success('Xero connected successfully!');
        }
        if (searchParams.get('error')) {
          toast.error(`Xero connection failed: ${searchParams.get('error')}`);
        }
        await Promise.all([checkStatus(), fetchCompanies(), fetchLinks()]);
      } catch (err) {
        console.error('Initialization error:', err);
        setError(err.message || 'Failed to initialize');
        setLoading(false);
      }
    };
    init();
  }, [searchParams]);

  const checkStatus = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/xero/status`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setConnected(data.connected);
        if (data.connected) {
          fetchContacts();
        }
      }
    } catch (error) {
      console.error('Failed to check status:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchContacts = async () => {
    setLoadingContacts(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/xero/contacts`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setContacts(data.contacts || []);
        setTenantId(data.tenant_id || '');
        setTenantName(data.tenant_name || '');
      }
    } catch (error) {
      console.error('Failed to fetch contacts:', error);
    } finally {
      setLoadingContacts(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/companies`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setCompanies(data || []);
      }
    } catch (error) {
      console.error('Failed to fetch companies:', error);
    }
  };

  const fetchLinks = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/xero/links`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setLinks(data.links || []);
      }
    } catch (error) {
      console.error('Failed to fetch links:', error);
    }
  };

  const connectXero = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/xero/auth-url`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        window.location.href = data.auth_url;
      }
    } catch (error) {
      toast.error('Failed to connect to Xero');
    }
  };

  const handleLinkContact = async (contact) => {
    if (!selectedCompany) {
      toast.error('Please select a company');
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/xero/link-contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          company_id: selectedCompany,
          xero_contact_id: contact.ContactID,
          xero_contact_name: contact.Name,
          xero_tenant_id: tenantId
        })
      });

      if (response.ok) {
        toast.success('Contact linked successfully');
        setLinkingContact(null);
        setSelectedCompany('');
        fetchLinks();
        fetchContacts();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to link contact');
      }
    } catch (error) {
      toast.error('Failed to link contact');
    }
  };

  const handleRemoveLink = async (linkId) => {
    if (!window.confirm('Are you sure you want to remove this link?')) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/xero/links/${linkId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Link removed');
        fetchLinks();
        fetchContacts();
      }
    } catch (error) {
      toast.error('Failed to remove link');
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Billing (Xero)">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout title="Billing (Xero)">
        <div className="flex flex-col items-center justify-center h-64 text-center">
          <AlertTriangle className="h-16 w-16 text-destructive mb-4" />
          <h3 className="text-lg font-mono font-bold mb-2">Error Loading Page</h3>
          <p className="text-muted-foreground mb-4">{error}</p>
          <Button onClick={() => window.location.reload()}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Reload Page
          </Button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Billing (Xero)">
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 rounded-lg p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className="h-12 w-12 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                <CreditCard className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h2 className="text-xl font-mono font-bold mb-2">Xero Billing Integration</h2>
                <p className="text-sm text-muted-foreground">
                  Link Xero contacts to your portal companies so customers can view their invoices.
                </p>
                {tenantName && (
                  <p className="text-sm text-primary mt-1">
                    Connected to: <span className="font-bold">{tenantName}</span>
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {connected ? (
                <span className="flex items-center gap-2 px-3 py-1.5 bg-primary/10 text-primary rounded-full text-sm font-mono">
                  <CheckCircle2 className="h-4 w-4" />
                  Connected
                </span>
              ) : (
                <Button onClick={connectXero} className="bg-primary text-primary-foreground">
                  <Link2 className="h-4 w-4 mr-2" />
                  Connect Xero
                </Button>
              )}
            </div>
          </div>
        </div>

        {!connected ? (
          <div className="bg-card border border-border rounded-lg p-12 text-center">
            <Link2 className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-mono font-bold mb-2">Connect Xero</h3>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              Connect your Xero account to enable invoice viewing for your customers.
            </p>
            <Button onClick={connectXero} className="bg-primary text-primary-foreground">
              <ExternalLink className="h-4 w-4 mr-2" />
              Connect to Xero
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Xero Contacts */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-mono font-bold">Xero Contacts</h3>
                <Button variant="outline" size="sm" onClick={fetchContacts}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh
                </Button>
              </div>

              <div className="bg-card border border-border rounded-lg divide-y divide-border max-h-[600px] overflow-y-auto">
                {loadingContacts ? (
                  <div className="p-8 text-center">
                    <Loader2 className="h-6 w-6 animate-spin text-primary mx-auto" />
                  </div>
                ) : contacts.length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground">
                    No contacts found in Xero
                  </div>
                ) : (
                  contacts.map((contact) => (
                    <div key={contact.ContactID} className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-mono font-bold truncate">{contact.Name}</h4>
                          {contact.EmailAddress && (
                            <p className="text-xs text-muted-foreground">{contact.EmailAddress}</p>
                          )}
                          <span className={`inline-block mt-2 px-2 py-0.5 rounded-full text-xs font-mono ${
                            contact.is_linked 
                              ? 'bg-primary/10 text-primary' 
                              : 'bg-muted text-muted-foreground'
                          }`}>
                            {contact.is_linked ? 'Linked' : 'Not Linked'}
                          </span>
                        </div>
                        {!contact.is_linked && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => setLinkingContact(contact)}
                          >
                            Link
                          </Button>
                        )}
                      </div>

                      {/* Link Form */}
                      {linkingContact?.ContactID === contact.ContactID && (
                        <div className="mt-4 p-4 bg-background rounded-md border border-border">
                          <Label className="font-mono text-sm">Link to Company</Label>
                          <select
                            value={selectedCompany}
                            onChange={(e) => setSelectedCompany(e.target.value)}
                            className="w-full mt-1 px-3 py-2 bg-card border border-border rounded-md font-mono text-sm"
                          >
                            <option value="">Select a company...</option>
                            {companies.map((company) => (
                              <option key={company.company_id} value={company.company_id}>
                                {company.name}
                              </option>
                            ))}
                          </select>
                          <div className="flex gap-2 mt-3">
                            <Button 
                              size="sm" 
                              onClick={() => handleLinkContact(contact)}
                              disabled={!selectedCompany}
                            >
                              Confirm
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => {
                                setLinkingContact(null);
                                setSelectedCompany('');
                              }}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Current Links */}
            <div className="space-y-4">
              <h3 className="text-lg font-mono font-bold">Contact Links</h3>
              <div className="bg-card border border-border rounded-lg divide-y divide-border max-h-[600px] overflow-y-auto">
                {links.length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground">
                    <Building className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No contacts linked yet</p>
                    <p className="text-sm mt-1">Link Xero contacts to your portal companies</p>
                  </div>
                ) : (
                  links.map((link) => (
                    <div key={link.link_id} className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-mono font-bold truncate">{link.xero_contact_name || 'Unknown Contact'}</h4>
                          <div className="flex items-center gap-2 mt-2">
                            <Building className="h-3 w-3 text-muted-foreground" />
                            <span className="text-sm text-primary">{link.company_name || 'Unknown Company'}</span>
                          </div>
                          {link.created_at && (
                            <p className="text-xs text-muted-foreground mt-1">
                              Linked: {new Date(link.created_at).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                          onClick={() => handleRemoveLink(link.link_id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

export default XeroBillingAdmin;
